/**
 * Shift reports — QA report §4.2.
 *
 * "Print all of the shift's receipts, or only mine." A shift is a window of
 * wall-clock time on a day — the morning, the evening, the night, or any two
 * times the accountant names — and the report is every receipt issued in it,
 * by everybody or by one person, with the money summed the way a cashier
 * hands over a till: what was billed, what was actually taken (by method),
 * what is still owed on deferred receipts, and what was voided.
 *
 * Pure: the window arithmetic and the sums are here, tested; the screen
 * (features/hospitalShared/ShiftReport.jsx) only fetches and draws.
 */

/** The hospital's usual shifts. `to` of 24:00 is midnight at the end of the day. */
export const SHIFT_PRESETS = Object.freeze([
  { id: 'morning', label: 'الوردية الصباحية', from: '08:00', to: '16:00' },
  { id: 'evening', label: 'الوردية المسائية', from: '16:00', to: '24:00' },
  { id: 'night', label: 'الوردية الليلية', from: '00:00', to: '08:00' },
  { id: 'day', label: 'اليوم كله', from: '00:00', to: '24:00' },
]);

export const METHOD_LABELS = Object.freeze({ cash: 'نقداً', card: 'بطاقة', wallet: 'محفظة', transfer: 'تحويل' });

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function minutesOf(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min > 0)) return null;
  return h * 60 + min;
}

/**
 * The window in local time. A shift whose end is not after its start runs
 * past midnight (22:00 → 06:00 ends the next morning).
 *
 * @param {string} dateKey 'YYYY-MM-DD' — the day the shift starts
 * @returns {{ from: Date, to: Date } | null}
 */
export function shiftWindow(dateKey, fromHHMM, toHHMM) {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ''));
  const start = minutesOf(fromHHMM);
  let end = minutesOf(toHHMM);
  if (!day || start === null || end === null) return null;
  if (end <= start) end += 24 * 60;
  const base = new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]), 0, 0, 0, 0);
  return {
    from: new Date(base.getTime() + start * 60000),
    to: new Date(base.getTime() + end * 60000),
  };
}

/** The preset a moment falls in (the full day never wins over a real shift). */
export function currentShift(now = new Date()) {
  const minutes = now.getHours() * 60 + now.getMinutes();
  return (
    SHIFT_PRESETS.filter((p) => p.id !== 'day').find((p) => {
      const from = minutesOf(p.from);
      const to = minutesOf(p.to);
      return minutes >= from && minutes < to;
    }) || SHIFT_PRESETS[3]
  );
}

/**
 * The sums a cashier hands over.
 *
 * Voided receipts are counted and listed but carry no money: the number is
 * used up, the money was never kept. A receipt without amounts (a desk that
 * does not deal in money) contributes nothing rather than NaN.
 */
export function summariseShift(receipts = []) {
  const out = {
    count: 0,
    voided: 0,
    cashCount: 0,
    deferredCount: 0,
    total: 0,
    paid: 0,
    remaining: 0,
    byMethod: {},
    byIssuer: [],
  };
  const issuers = new Map();

  for (const r of receipts) {
    out.count += 1;
    if (r.status === 'void') {
      out.voided += 1;
      continue;
    }
    const total = round2(r.total);
    const paid = round2(r.paidAmount ?? (r.status === 'collected' ? r.total : 0));
    const remaining = round2(Math.max(0, total - paid));
    out.total = round2(out.total + total);
    out.paid = round2(out.paid + paid);
    out.remaining = round2(out.remaining + remaining);
    if (r.receiptType === 'deferred') out.deferredCount += 1;
    else out.cashCount += 1;
    if (paid > 0) {
      const method = r.paymentMethod || 'cash';
      out.byMethod[method] = round2((out.byMethod[method] || 0) + paid);
    }

    const key = String(r.issuedBy ?? 'unknown');
    const issuer = issuers.get(key) || { id: r.issuedBy ?? null, name: r.signatureName || '—', count: 0, total: 0, paid: 0 };
    issuer.count += 1;
    issuer.total = round2(issuer.total + total);
    issuer.paid = round2(issuer.paid + paid);
    issuers.set(key, issuer);
  }

  out.byIssuer = Array.from(issuers.values()).sort((a, b) => b.paid - a.paid || a.name.localeCompare(b.name, 'ar'));
  return out;
}

/** Oldest first, the order a till is counted in. */
export const chronological = (receipts = []) =>
  [...receipts].sort((a, b) => Date.parse(a.issuedAt || 0) - Date.parse(b.issuedAt || 0));
